<template>
<div class="relative">
	<div class="fixed white" style="width: 100%" id="tool-bar">
	<v-row style="width: 100%" align="center" justify="start" class="ma-0">
		<v-col cols="auto" sm="6" xs="6" md="6" lg="auto">
			<slot name="breadcrumbs"></slot>
		</v-col>
		<v-spacer></v-spacer>
        <v-col cols="auto">
			<!-- export -->
			<v-menu offset-y>
				<template v-slot:activator="{ on }">
					<v-btn dark color="printingColor" class="px-2" v-on="on">
						<span class="d-flex align-center">
							<v-icon color="white">mdi-export-variant</v-icon>
							<span style="font-size: 14px">
								{{$t('TrailBalancePage.export')}}
							</span>
						</span>
					</v-btn>
				</template>

				<bee-list>
					<!-- excel -->
					<bee-list-item
						context="trial_balance"
						:item="exportItems[0]"
						dense
						@click="exportAs('excel')"
					></bee-list-item>

					<!-- excel (brief) -->
					<bee-list-item
						context="trial_balance"
						:item="exportItems[1]"
						dense
						@click="exportAs('excel', true)"
					></bee-list-item>

					<!-- pdf -->
					<bee-list-item
						context="trial_balance"
						:item="exportItems[2]"
						dense
						@click="exportAs('pdf')"
					></bee-list-item>
					
					<!-- pdf (brief) -->
					<bee-list-item
						context="trial_balance"
						:item="exportItems[3]"
						dense
						@click="exportAs('pdf', true)"
					></bee-list-item>
				</bee-list>
			</v-menu>
		</v-col>
	</v-row>
	<v-layout wrap class="d-flex flex-row my-border rounded-lg mx-3 mb-3 align-center">
		<v-col cols="auto" lg="auto" md="auto" class="pe-lg-0">
			<bee-account-autocomplete
				:label="$t('TrailBalancePage.search-about-account-number-or-name')"
				:items="configs.accounts"
				v-model="account_id"
				ref="BeeAutocomplete"
				@keydown-enter-prevent="search()"
				:with-balance="true"
				:hide-details="true"
				clearable
				@change="onChange('account_id')"
				context="trial_balance" 
				:action-name="actions.GET"
			></bee-account-autocomplete>
		</v-col>
		<v-col cols="auto" lg="auto" md="auto" sm="6" xs="12" class="pe-lg-0">
			<bee-account-autocomplete
				:label="$t('TrailBalancePage.from-account')"
				:items="configs.accounts"
				v-model="from_accounting_no"
				:with-balance="true"
				item-value="accounting_no"
				@keydown-enter-prevent="search()"
				:hide-details="true"
				@change="onChange('from_accounting_no')"
				context="trial_balance" 
				:action-name="actions.GET"
			></bee-account-autocomplete>
		</v-col>
		<v-col cols="auto" lg="auto" md="auto" sm="6" xs="12" class="pe-lg-0">
			<bee-account-autocomplete
				:label="$t('TrailBalancePage.to-account')"
				:items="configs.accounts"
				v-model="to_accounting_no"
				:with-balance="true"
				item-value="accounting_no"
				@keydown-enter-prevent="search()"
				:hide-details="true"
				@change="onChange('to_accounting_no')"
				context="trial_balance" 
				:action-name="actions.GET"
			></bee-account-autocomplete>
		</v-col>
		<v-col cols="auto" lg="auto" md="auto" sm="6" xs="12" class="d-flex align-center pe-lg-0">
			<bee-checkbox
				v-model="show_parent_accounts"
				:label="$t('TrailBalancePage.show-main-accounts')"
				class="small-font-size mt-0" 
			></bee-checkbox>
		</v-col>
		<v-col cols="auto" lg="auto" md="auto" sm="6" xs="12" class="d-flex align-center pe-lg-0">
			<bee-checkbox
				v-model="show_non_zero_accounts_only"
				:label="$t('TrailBalancePage.balanced-accounts-only')"
				class="small-font-size mt-0" 
			></bee-checkbox>
		</v-col>
		<v-col cols="auto" lg="auto" md="auto" sm="6" class="pe-lg-0 d-flex align-column">
				<bee-checkbox
					v-model="show_all_columns"
					:label="$t('TrailBalancePage.show-all-columns')"
					class="small-font-size mt-0"
				></bee-checkbox>
			</v-col>
		<v-spacer></v-spacer>
		<v-col cols="auto" lg="auto" md="auto" sm="6" xs="12" class="d-flex align-center justify-end">
			<bee-btn  
				@click="search()"
				:content="$t('TrailBalancePage.refresh')"
				color="blueGray"
				outer-class="me-3"
				:loading="loading"
				context="trial_balance"
				:action-name="actions.GET"
			></bee-btn>
			<bee-btn  
				@click="cancel()"
				:content="$t('TrailBalancePage.cancel')"
				color="deletingColor"
				:loading="loading"
				context="trial_balance"
				:action-name="actions.GET"
			></bee-btn>
		</v-col>
	</v-layout>
	</div>

    <v-row class="ma-auto absolute" id="main" style="width: 100%; height: auto">
		<v-card-text class="px-3 pt-0">
			<bee-simple-table :with-color="false" :zebra="zebra" :hide-footer="true" empty-state="dashes"
				:fixed-header="true" 
				context="trial_balance"
				:action-name="actions.GET"
				:headers="showHeaders" 
				:items="trial_balance_rows"
				:configs="configs"
				:highlighted-expression="(item)=>{return item.is_parent ? 'orangeLighten5' : ''}"
				:right-click-menu-items="[{name: 'statement', disabled: false, text: $t('TrailBalancePage.account-statement'), action_name: actions.GET}]"         
				:show-menu-actions="true"
				@statement="statement($event)"
			>
			</bee-simple-table>
			<bee-simple-table
				context="trial_balance"
				:action-name="actions.GET"
				class="pt-5" :with-color="true" :zebra="false" empty-state="dashes" :hide-footer="true" :configs="configs"
				:headers="sum_result_headers"
				:items="custom_statement_sum_result || []"
			>
			</bee-simple-table>
			<bee-simple-table
				context="trial_balance"
				:action-name="actions.GET"
				class="pt-5" :with-color="true" :zebra="false" empty-state="dashes" :hide-footer="true" :configs="{...configs, currencies: configs.currencies.filter(c=>c.id != configs.financial_file.default_currency_id)}"
				:headers="exchange_currencies_headers"
				:items="custom_statement_sum_result || []"
				@equivalent-currency-id-changed="changeSumDifferenceEquivalent($event)"
			>
			</bee-simple-table>
		</v-card-text>
    </v-row>
</div>
</template>
<script>
import  store  from "../../../store/index.js";
import {  mapState } from "vuex";
import actions from "../../../permissions/actions-names.js"
import ls from "../../../settings/localStorageExtension"
export default {
data: () => ({
	show_all_columns: false,
	actions,
	sum_difference_equivalent: { amount: null, currency_id: null, money_type: null},
	show_non_zero_accounts_only: true,
	zebra: true,
	loading: false,
	show_parent_accounts: null,
	account_id: null,
	from_accounting_no: null,
	to_accounting_no: null
}),
methods: {
	cancel() {
		this.loading = true
		store.dispatch("statements/createTrialBalance", {account_id: null, show_parent_accounts: false, show_non_zero_accounts_only: true}).finally(()=>{
			this.loading = false,
			this.reSet()
		});
	},
	changeSumDifferenceEquivalent(toCurrencyId) {
		if(toCurrencyId)
		{
			let sum_difference = this.trial_balance?.custom_statement_sum_result?.sum_difference
			if(sum_difference?.amount)
			{
				let amount = sum_difference.amount
				let fromCurrecyId = sum_difference.currency_id
				store.dispatch("currencies/exchangeToCurrency",{toCurrencyId, amount, fromCurrecyId})
					.then(response => {
						_.set(this.sum_difference_equivalent,"amount", response.amount)
						_.set(this.sum_difference_equivalent,"currency_id", response.currency_id)
						_.set(this.sum_difference_equivalent,"money_type", sum_difference.money_type)
					})
			}
		}
	},
	search(){
		this.loading = true
		store.dispatch("statements/createTrialBalance", {
			account_id: this.account_id, 
			show_parent_accounts: this.show_parent_accounts,
			show_non_zero_accounts_only: this.show_non_zero_accounts_only,
			from_accounting_no: this.from_accounting_no,
			to_accounting_no: this.to_accounting_no,
		})
		.finally(()=>{
			this.loading = false
			this.zebra = !this.show_parent_accounts
			this.sum_difference_equivalent = { amount: null, currency_id: null, money_type: null}
		})
	},
	statement(row){
		let routeData = this.$router.resolve({name: 'statement', params: {id: row.account_id}});
		window.open(routeData.href, '_blank');
	},
	reSet(){
		this.account_id = null,
		this.show_parent_accounts = false
		this.zebra = true,
		this.from_accounting_no = null,
		this.to_accounting_no = null,
		this.show_non_zero_accounts_only = true
		this.$refs['BeeAutocomplete'].$refs.BeeAccountAutocomplete.$refs.BeeAutocomplete.focus()
		this.sum_difference_equivalent = { amount: null, currency_id: null, money_type: null}
	},
	onChange(string)
	{
		if(string == 'account_id')
		{
			this.from_accounting_no = null
			this.to_accounting_no = null
		}
		else
		{
			this.account_id = null
		}
	},

	exportAs(type, isBrief){
		const params = {
			type,
			financialFileId: this.configs.financial_file.id,
			userId: ls.getLocalUser().id,
			showParentAccounts: this.show_parent_accounts,
			showNonZeroAccountsOnly: this.show_non_zero_accounts_only,
			brief: isBrief ? isBrief : false,
		};
		if(this.account_id) {
			params.accountId = this.account_id;
		} else if (this.from_accounting_no && this.to_accounting_no) {
			params.fromAccountingNo = this.from_accounting_no;
			params.toAccountingNo = this.to_accounting_no;
		}
		this.$store.dispatch('reports/TrailBalanceReport', params);
	}
},
mounted(){
	window.observer.observe(document.querySelector("#tool-bar"))
	this.reSet()
},
computed: {
	...mapState("configs", ["configs"]),
	...mapState("statements" , ["trial_balance"]),
	exportItems() {
		return [
			{
				text: this.$t('TrailBalancePage.export-as-excel'),
				icon: 'mdi-microsoft-excel',
				context: 'trial_balance',
				action_name: actions.EXPORT_AS_PDF
			},
			{
				text: this.$t('TrailBalancePage.export-as-excel') + ' ' + this.$t('TrailBalancePage.brief'),
				icon: 'mdi-microsoft-excel',
				context: 'trial_balance',
				action_name: actions.EXPORT_AS_PDF
			},
			{
				text: this.$t('TrailBalancePage.print-trial-balance'),
				icon: 'mdi-printer',
				context: 'trial_balance',
				action_name: actions.EXPORT_AS_PDF
			},
			{
				text: this.$t('TrailBalancePage.print-trial-balance') + ' ' + this.$t('TrailBalancePage.brief'),
				icon: 'mdi-printer',
				context: 'trial_balance',
				action_name: actions.EXPORT_AS_PDF
			},
		]
	},
	showHeaders(){
		return this.show_all_columns ? this.headers : this.subHeaders
	},
	custom_statement_sum_result(){
		return this.trial_balance.custom_statement_sum_result ? [].concat({...this.trial_balance.custom_statement_sum_result, sum_difference_equivalent: this.sum_difference_equivalent }): []
	},
	file_is_locked(){
        return this.configs?.financial_file?.is_locked 
	},
	trial_balance_rows(){
		return this.trial_balance.trial_balance_rows || []
	},

	sum_result_headers(){
		return [
			{ text: '', value: '', type: '', width: '30%' },
			{
				text: this.$t('TrailBalancePage.sum-result-headers.debit-sum'),
				value: 'sum_debits',
				type: 'money',
				width: '20%'
			},
			{
				text: this.$t('TrailBalancePage.sum-result-headers.credit-sum'),
				value: 'sum_credits',
				type: 'money',
				width: '20%',
				editable: true 
			},
			{
				text: this.$t('TrailBalancePage.sum-result-headers.debit-balance'),
				value: 'debit_balance',
				type: 'money',
				width: '15%'
			},
			{
				text: this.$t('TrailBalancePage.sum-result-headers.credit-balance'),
				value: 'credit_balance',
				type: 'money',
				width: '15%',
				editable: true 
			},
		]
	},

	exchange_currencies_headers(){
		return [
			{ text: '', value: '', type: '', width: '50%' },
			{
				text: this.$t('TrailBalancePage.exchange-currencies-headers.difference'),
				value: 'sum_difference',
				type: 'money',
				width: '20%',
				editable: true
			},
			{
				text: this.$t('TrailBalancePage.exchange-currencies-headers.equivalent-currency'),
				value: 'sum_difference_equivalent.currency_id',
				type: 'select',
				width: '10%'
			},
			{
				text: this.$t('TrailBalancePage.exchange-currencies-headers.currency-equivalent-balance'),
				value: 'sum_difference_equivalent',
				type: 'money',
				width: '20%',
				editable: true
			},
		]
	},

	headers(){
		return [
			// cannot use dublicate same value for configs  ... fast fix spacae and trim other side clean
			{
				text: this.$t('TrailBalancePage.headers.account-no'),
				value: ' account_id',
				type: 'accounts-autocomplete',
				width: '10%',
				settings: { itemText: 'accounting_no' }
			},
			{
				text: this.$t('TrailBalancePage.headers.account-name'),
				value: 'account_id',
				type: 'accounts-autocomplete',
				width: '10%',
				settings: { itemText: 'name' }
			},
			{
				text: this.$t('TrailBalancePage.headers.parent-account'),
				value: 'parent_account_id',
				type: 'accounts-autocomplete',
				width: '10%',
				settings: { itemText: 'name' }
			},
			{
				text: this.$t('TrailBalancePage.headers.debit-sum'),
				value: 'debit_sum',
				type: 'money',
				width: '20%',
				settings: { emptyState: 'none' }
			},
			{
				text: this.$t('TrailBalancePage.headers.credit-sum'),
				value: 'credit_sum',
				type: 'money',
				width: '20%',
				settings: { emptyState: 'none' }
			},
			{
				text: this.$t('TrailBalancePage.headers.debit-balance'),
				value: 'debit_balance',
				type: 'money',
				width: '15%',
				settings: { emptyState: 'none' }
			},
			{
				text: this.$t('TrailBalancePage.headers.credit-balance'),
				value: 'credit_balance',
				type: 'money',
				width: '15%',
				settings: { emptyState: 'none' }
			},
		]
	},

	subHeaders(){
		return [
			// cannot use dublicate same value for configs  ... fast fix spacae and trim other side clean
			{
				text: this.$t('TrailBalancePage.headers.account-no'),
				value: ' account_id',
				type: 'accounts-autocomplete',
				width: '10%',
				settings: { itemText: 'accounting_no' }
			},
			{
				text: this.$t('TrailBalancePage.headers.account-name'),
				value: 'account_id',
				type: 'accounts-autocomplete',
				width: '20%',
				settings: { itemText: 'name' }
			},
			{
				text: this.$t('TrailBalancePage.headers.parent-account'),
				value: 'parent_account_id',
				type: 'accounts-autocomplete',
				width: '20%',
				settings: { itemText: 'name' }
			},
			{
				text: this.$t('TrailBalancePage.headers.debit-balance'),
				value: 'debit_balance',
				type: 'money',
				width: '25%',
				settings: { emptyState: 'none' }
			},
			{
				text: this.$t('TrailBalancePage.headers.credit-balance'),
				value: 'credit_balance',
				type: 'money',
				width: '25%',
				settings: { emptyState: 'none' }
			},
		]
	},
},
async beforeRouteEnter(to, from, next) {
	let promises = []

	let object = JSON.parse(JSON.stringify(store.state.configs.configs))
	if(Object.keys(object).length == 0)
		promises.push(store.dispatch("configs/fetchAll"))
	
	if(promises.length)
		await Promise.all(promises)
	next();
}
};
</script>
<style scoped>
.small {
	max-width: 600px;
	margin:  150px auto;
}
.small-font-size .v-label { 
	font-size: 0.8rem !important;
	padding-right: 0px !important;
}
.my-border {
	border:1px solid !important;
	border-color: #f1f1f1 !important;
}
</style>